import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, Ticket as TicketIcon } from "lucide-react";

import { usePublicTicketProducts } from "@/hooks/use-public-ticket-products";

export const Route = createFileRoute("/tickets/")({
  head: () => ({
    meta: [
      { title: "All Tickets — Helsinki Sightseeing | What" },
      {
        name: "description",
        content:
          "Buy instant tickets for Helsinki hop-on hop-off buses, sightseeing boats, and combo tours. Book online, ride today.",
      },
      { property: "og:title", content: "All Tickets — Helsinki Sightseeing" },
      {
        property: "og:description",
        content: "Skip the line — boats, buses, and combo tours in Helsinki.",
      },
      { property: "og:url", content: "https://whatfinland.com/tickets" },
    ],
    links: [
      { rel: "canonical", href: "https://whatfinland.com/tickets" },
    ],
  }),
  component: TicketsIndexPage,
});

function TicketsIndexPage() {
  const tickets = usePublicTicketProducts();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <header className="mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-navy/5 border border-navy/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-navy/70">
            <TicketIcon className="size-3.5" />
            Instant Tickets
          </div>
          <h1 className="mt-4 font-heading text-navy text-4xl sm:text-5xl tracking-tight">
            All Tickets
          </h1>
          <p className="mt-3 text-navy/65 text-[14px] sm:text-[15px] max-w-2xl">
            Skip the line — pick a date and ride today. Sightseeing boats, hop-on hop-off buses, and combo passes across Helsinki.
          </p>
        </header>

        {tickets.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy/15 bg-white p-12 text-center">
            <p className="text-navy/60 text-sm">No tickets available right now. Please check back soon.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {tickets.map((t) => (
              <Link
                key={t.id}
                to="/tickets/$slug"
                params={{ slug: t.ticketSlug! }}
                className="group rounded-2xl overflow-hidden border border-navy/10 bg-white shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-500"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-navy/5">
                  {t.image && (
                    <img
                      src={t.image}
                      alt={t.name}
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent" />
                  <span className="absolute top-3 left-3 bg-white text-navy text-[9px] font-mono uppercase tracking-[0.2em] px-2.5 py-1 rounded-full shadow-sm">
                    Instant Ticket
                  </span>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/75">
                      {t.category}
                    </p>
                    <h2 className="font-heading text-lg leading-tight mt-1 [text-shadow:0_2px_10px_rgba(8,15,40,0.7)]">
                      {t.name}
                    </h2>
                  </div>
                </div>
                <div
                  style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                >
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-navy/75">
                    <Clock className="size-3.5 text-teal shrink-0" />
                    <span className="truncate">{t.area}</span>
                  </span>
                  {t.price && (
                    <span className="shrink-0 text-[12px] font-semibold tabular-nums px-2.5 py-1 rounded-full bg-navy text-white">
                      {t.price}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
